import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import Box from '@node-space/storybook-components/dist/Box'
import Callout from '@node-space/storybook-components/dist/Callout'
import { TableRow } from '@node-space/storybook-components/dist/components/SimpleTable'
import ImageIcon from '@node-space/storybook-components/dist/ImageIcon'
import { useSidePanel } from '@node-space/storybook-components/dist/SidePanel'
import {
  SimpleTableV2,
  useTableCheckbox,
} from '@node-space/storybook-components/dist/SimpleTableV2'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import Text from '@node-space/storybook-components/dist/Text'
import { DateFormat } from 'constants/General'
import { ApprovalStatuses, PendingTransactionsResponse } from 'types/approvals'
import { SelectOption } from 'types/types'
import mockInitiatorOptions from '../../../../../__mocks__/Approvals/initiatorOptions.json'
import ApprovalFilterBar from '../ApprovalFilterBar'
import { SidePanelSummary } from '../SidePanel/SidePanelSummary'

const rowHeaderWidth = 'px-8 w-1/4'

interface PendingApprovalsQueryInfo {
  pendingApprovalTransactions: PendingTransactionsResponse[]
  isPendingApprovalsError: boolean
  isPendingApprovalSuccess: boolean
  isFetchingPendingApprovals: boolean
}

interface PendingTransactionsProps {
  isFetchingPendingApproval?: boolean
  pendingApprovalsQueryInfo?: PendingApprovalsQueryInfo
  setCheckedRowData: Dispatch<SetStateAction<unknown[]>>
  canManageApproval: boolean
  filterCurrencyOptions: SelectOption[]
  isApprovalPreferenceDisabled: boolean
}

export const PendingTransactions = ({
  pendingApprovalsQueryInfo,
  setCheckedRowData,
  canManageApproval,
  filterCurrencyOptions,
  isApprovalPreferenceDisabled,
}: PendingTransactionsProps) => {
  const { t } = useTranslation()

  // TODO: filterbar search functionality with BE integration in phase 3
  const [searchTerm, setSearchTerm] = useState('')
  const [searchCurrency, setSearchCurrency] = useState('')

  const {
    pendingApprovalTransactions = [],
    isPendingApprovalsError,
    isFetchingPendingApprovals,
  } = pendingApprovalsQueryInfo
  const initiatorOptions = mockInitiatorOptions

  const { isCheckAll, checkedItems, handleCheckAll, handleCheckRow, rowsClicked, clearSelection } =
    useTableCheckbox(pendingApprovalTransactions)

  useEffect(() => {
    setCheckedRowData(checkedItems)
  }, [rowsClicked])

  const onSearchQueryChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchCurrency(e.currentTarget.value)
  }

  const {
    isSidePanelOpen: isPendingApprovalDetailsOpen,
    sidePanelData: pendingApprovalDetails,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(pendingApprovalTransactions)

  const openSidePanelSummary = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const transactionsData = (): TableRow[] => {
    return pendingApprovalTransactions?.map((approvalTransaction, index) => ({
      rowData: [
        {
          cellData: (
            <Box flex alignItems="center" gap={12} className="relative">
              <ImageIcon
                width={24}
                height={24}
                hasBackgroundColor={false}
                src={`${
                  process.env.ICONS_URL
                }${approvalTransaction?.currency?.code?.toLowerCase()}.svg`}
              />
              <Box className="relative">
                <Text size="sm" className="text-500">
                  {`${approvalTransaction?.amount} ${approvalTransaction?.currency?.code}`}
                </Text>
                <Text size="sm" color="disabled">
                  {approvalTransaction?.walletName}
                </Text>
              </Box>
            </Box>
          ),
          className: `py-4 px-8`,
        },
        {
          cellData: (
            <Box>
              <Text size="sm" className="text-500">
                {approvalTransaction?.fiatWithdrawalDetails?.accountNumber}
              </Text>
              <Text size="sm" color="disabled">
                {approvalTransaction?.fiatWithdrawalDetails?.beneficiaryName}
              </Text>
            </Box>
          ),
        },
        {
          cellData: (
            <Box>
              <Text size="sm" className="text-500">
                {approvalTransaction?.initiatedBy}
              </Text>
              <Text size="sm" color="disabled">
                {dayjs(approvalTransaction?.actionedAt).format(DateFormat.D_MMM_YYYY_AT_HH_MM)}
              </Text>
            </Box>
          ),
          className: `px-8`,
        },
      ],
      onClick: () => openSidePanelSummary(index),
    }))
  }

  const pendingTransactionTableInfo = transactionsData()

  return (
    <>
      {isApprovalPreferenceDisabled && (
        <Box paddingT={16}>
          <Callout state="warning" message={t('approvals.disableApprovalCalloutDisclaimer')} />
        </Box>
      )}
      {!!pendingApprovalDetails && (
        <SidePanelSummary
          isOpen={isPendingApprovalDetailsOpen}
          paging={paging}
          onClose={() => setIsSidePanelOpen(false)}
          transactionDetails={pendingApprovalDetails}
        />
      )}
      {isPendingApprovalsError ? (
        <StatusCard status="error" headline={t('oopsSomethingWentWrong')} />
      ) : !isFetchingPendingApprovals && !pendingTransactionTableInfo?.length ? (
        <StatusCard
          headline={t('approvals.emptyStateHeadline', {
            approvalStatus: ApprovalStatuses.PENDING.toLowerCase(),
          })}
          description={t('approvals.emptyStateDescription', {
            approvalStatus: ApprovalStatuses.PENDING.toLowerCase(),
          })}
        />
      ) : (
        <>
          <ApprovalFilterBar
            searchTerm={searchTerm}
            onSearchTermChange={onSearchQueryChanged}
            searchCurrency={searchCurrency}
            onSearchCurrencyChange={currency => setSearchCurrency(currency)}
            initiatorOptions={initiatorOptions}
            currencyOptions={filterCurrencyOptions}
          />
          <SimpleTableV2
            columns={[
              {
                headerTitle: t('payment'),
                className: rowHeaderWidth,
              },
              {
                headerTitle: t('to'),
              },
              {
                headerTitle: t('approvals.initiatedBy'),
                className: rowHeaderWidth,
              },
            ]}
            tableData={pendingTransactionTableInfo}
            enableCheckbox={canManageApproval}
            checkboxConfig={{ isCheckAll, handleCheckAll, rowsClicked, handleCheckRow }}
          />
        </>
      )}
    </>
  )
}
